if (typeof MainHeader !== "function") {
  class MainHeader extends HTMLElement {
    constructor() {
      super();
      this._mounted = false;

      // bind once
      this.SCROLL_StickyHelper = null;
      this.RESIZE_SidebarHelper = null;

      // keep references so unmount can clean up
      this._listeners = [];
      this._drawerOpeners = [];
      this._stickyHeaderEl = null;

      this.mount();
    }

    mount() {
      if (this._mounted) return;
      this._mounted = true;

      /* ============================
        INERT SETUP
      ============================ */
      document.querySelectorAll("#main > div").forEach((elm) => {
        if (!elm.classList.contains("no-inert")) elm.setAttribute("data-js-inert", "");
      });
      window.inertElems = document.querySelectorAll("[data-js-inert]");

      /* ============================
        STICKY HEADER
      ============================ */
      if (this.hasAttribute("data-sticky-header")) {
        this._createStickyHeader();
        window.lst = window.scrollY;
        window.lhp = 0;

        const stickyHeaderDeskBound = this.classList.contains("site-header--style-one-row")
          ? this.querySelector(".header__top")
          : this.querySelector(".header__bottom");
        const stickyHeaderMobileBound = this.querySelector(".header__top");

        this.SCROLL_StickyHelper = () => {
          const st = window.scrollY;
          const stickyHeader = this._stickyHeaderEl;
          if (!stickyHeader) return;

          const isAtTopOrHeaderVisible =
            st <= 0 ||
            (window.innerWidth >= 1024
              ? stickyHeaderDeskBound?.getBoundingClientRect().top >= 0
              : stickyHeaderMobileBound?.getBoundingClientRect().top >= 0);

          if (isAtTopOrHeaderVisible && stickyHeader.classList.contains("show")) {
            stickyHeader.classList.remove("show");
            return;
          }

          if (st < 0 || Math.abs(window.lst - st) <= 5) return;

          if (st > window.lhp) {
            if (st === 0 && stickyHeader.classList.contains("show")) {
              stickyHeader.classList.remove("show");
            } else if (st <= window.lst && !stickyHeader.classList.contains("show")) {
              window.lhp = stickyHeader.offsetTop;
              stickyHeader.classList.add("show");
            } else if (st > window.lst && stickyHeader.classList.contains("show")) {
              stickyHeader.classList.remove("show");
            }
          }

          window.lst = st;
        };

        window.addEventListener("scroll", this.SCROLL_StickyHelper, { passive: true });
        this._listeners.push(["scroll", window, this.SCROLL_StickyHelper]);
      }

      /* ============================
        DRAWER OPENERS (ADA)
      ============================ */
      this._wireDrawerOpeners();

      // Drawer placement tweaks from original code
      if (this.classList.contains("site-header--alignment-left") || this.classList.contains("site-header--style-one-row")) {
        const ss = document.getElementById("site-search-sidebar");
        ss?.classList.remove("sidebar--left");
        ss?.classList.add("sidebar--right");
      }
      if (this.classList.contains("site-header--style-one-row") && this.classList.contains("site-header--alignment-center")) {
        const ms = document.getElementById("site-menu-sidebar");
        ms?.classList.remove("sidebar--right");
        ms?.classList.add("sidebar--left");
      }

      /* ============================
        CLOSE DRAWERS
      ============================ */
      document.querySelectorAll("sidebar-drawer [data-js-close]").forEach((btn) => {
        const onClick = (e) => {
          e.preventDefault();
          const sidebar = e.target.closest(".sidebar");
          if (sidebar?.classList.contains("sidebar--opened")) sidebar.hide();
        };
        btn.addEventListener("click", onClick);
        this._listeners.push(["click", btn, onClick]);
      });

      const overlay = document.querySelector(".site-overlay");
      if (overlay) {
        const onOverlayClick = () => {
          document.querySelector(".sidebar--opened")?.hide();
        };
        overlay.addEventListener("click", onOverlayClick);
        this._listeners.push(["click", overlay, onOverlayClick]);
      }

      const onKeydownEsc = (e) => {
        if (e.keyCode === window.KEYCODES?.ESC) {
          document.querySelector(".sidebar--opened")?.hide();
        }
      };
      document.addEventListener("keydown", onKeydownEsc);
      this._listeners.push(["keydown", document, onKeydownEsc]);

      /* ============================
        RESIZE DRAWERS / CSS VARS
      ============================ */
      const rootHeight = document.getElementById("root-height");
      this.RESIZE_SidebarHelper = debounce(() => {
        if (!rootHeight) return;
        rootHeight.innerHTML = `:root {
          --window-height: ${window.innerHeight}px;
          --window-width: ${document.body.clientWidth}px;
        }`;
      }, 200);

      window.addEventListener("resize", this.RESIZE_SidebarHelper);
      this._listeners.push(["resize", window, this.RESIZE_SidebarHelper]);

      if (rootHeight) {
        rootHeight.innerHTML = `:root {
          --window-height: ${window.innerHeight}px;
          --window-width: ${document.body.clientWidth}px;
        }`;
      }

      document.body.classList.add("header-loaded");

      /* ============================
        MENU WIDTH SWITCH
      ============================ */
      this._previousWidth = window.innerWidth;
      this._checkMenuWidth();
      this._checkMenuWidth();

      const onResizeMenu = debounce(() => {
        const currentWidth = window.innerWidth;
        if (currentWidth < this._previousWidth) this._checkMenuWidth();
        this._previousWidth = currentWidth;
      }, 200);
      window.addEventListener("resize", onResizeMenu);
      this._listeners.push(["resize", window, onResizeMenu]);

      /* ============================
        SUBMENU MASONRY
      ============================ */
      document.querySelectorAll(".site-nav.style--classic .submenu-masonry").forEach((elm) => {
        if (!window.Macy) return;
        const submenuMacy = new Macy({
          container: elm,
          columns: parseInt(elm.getAttribute("data-columns"), 10),
          margin: 30,
        });
        setTimeout(() => submenuMacy.reInit(), 100);
      });

      /* ============================
        PREDICTIVE SEARCH
      ============================ */
      const featuresEl = document.getElementById("shopify-features");
      const predictiveEnabled = (() => {
        try {
          return featuresEl && JSON.parse(featuresEl.text).predictiveSearch;
        } catch {
          return false;
        }
      })();

      if (predictiveEnabled) {
        // FIXED SELECTOR: missing closing bracket in original
        document.querySelectorAll("[data-js-open-search-drawer]").forEach((elm) => {
          const onClick = (e) => {
            e.preventDefault();
            if (!document.body.classList.contains("predictive-script-loaded")) {
              document.body.classList.add("predictive-script-loaded");
              const predictiveSearchJS = document.createElement("script");
              predictiveSearchJS.src = KROWN.settings.predictive_search_script;
              document.head.appendChild(predictiveSearchJS);
            }
            setTimeout(() => document.querySelector("#search-form-sidebar")?.focus(), 10);
          };
          elm.addEventListener("click", onClick);
          this._listeners.push(["click", elm, onClick]);
        });
      } else {
        const sf = this.querySelector("search-form");
        if (sf) {
          const onClick = (e) => {
            e.preventDefault();
            window.location.href = KROWN.settings.routes.search_url;
          };
          sf.addEventListener("click", onClick);
          this._listeners.push(["click", sf, onClick]);
        }
      }

      /* ============================
        TOUCH MENU HELPERS
      ============================ */
      this._wireTouchMenus();

      /* ============================
        KEYBOARD MENU (ADA)
      ============================ */
      this._wireKeyboardMenus();
    }

    unmount() {
      if (!this._mounted) return;
      this._mounted = false;

      // remove listeners
      this._listeners.forEach(([evt, target, handler]) => {
        target.removeEventListener(evt, handler);
      });
      this._listeners = [];

      // remove sticky header
      this._stickyHeaderEl?.remove();
      this._stickyHeaderEl = null;

      // remove scroll handler if present
      if (this.SCROLL_StickyHelper) window.removeEventListener("scroll", this.SCROLL_StickyHelper);
      if (this.RESIZE_SidebarHelper) window.removeEventListener("resize", this.RESIZE_SidebarHelper);

      document.querySelector(".sticky-header")?.remove();
    }

    /* ============================
      PRIVATE HELPERS
    ============================ */

    _createStickyHeader() {
      const stickyHeader = document.createElement("div");
      stickyHeader.classList = "sticky-header site-header";

      if (this.classList.contains("site-header--style-one-row")) {
        stickyHeader.innerHTML = `<div class="container--large">
          <div class="${this.querySelector(".header__top")?.className || ""}" style="height:calc(var(--header-logo) + var(--header-vertical-space))">
            ${this.querySelector(".header__top")?.innerHTML || ""}
          </div>
        </div>`;
        stickyHeader.className = `sticky-header ${this.className}`;
        stickyHeader.classList.remove("site-header--absolute");
      } else {
        stickyHeader.innerHTML = `<div class="header__bottom header-container container--large portable-hide">
          ${this.querySelector(".header__bottom")?.innerHTML || ""}
          <div style="display:flex;gap:15px;">
            ${this.querySelector(".area--custom-icon") ? this.querySelector(".area--custom-icon").outerHTML : ""}
            ${this.querySelector(".area--search") ? this.querySelector(".area--search").outerHTML : ""}
            ${this.querySelector(".area--account") ? this.querySelector(".area--account").outerHTML : ""}
            ${this.querySelector(".area--cart") ? this.querySelector(".area--cart").outerHTML : ""}
          </div>
        </div>
        <div class="site-header element--hide-on-portable-plus">
          ${this.querySelector(".header-container--top")?.innerHTML || ""}
        </div>`;
      }

      document.body.append(stickyHeader);

      // de-dupe IDs in sticky header
      stickyHeader.querySelectorAll("[id]").forEach((elm) => {
        elm.id = `${elm.id}-sticky`;
      });
      stickyHeader.querySelectorAll(".site-nav [aria-controls]").forEach((elm) => {
        elm.setAttribute("aria-controls", `${elm.getAttribute("aria-controls")}-sticky`);
      });

      this._stickyHeaderEl = stickyHeader;
    }

    _wireDrawerOpeners() {
      document.querySelectorAll("[data-js-sidebar-handle][aria-controls]").forEach((opener) => {
        const sidebarId = opener.getAttribute("aria-controls");
        const sidebar = document.getElementById(sidebarId);
        if (!sidebar) return;

        const open = (e) => {
          e?.preventDefault?.();
          sidebar._lastOpener = opener;
          opener.setAttribute("aria-expanded", "true");
          sidebar.show();

          // focus into the drawer
          setTimeout(() => {
            sidebar.querySelector("[data-js-close]")?.focus();
          }, 0);
        };

        const onClick = (e) => open(e);

        const onKeyDown = (e) => {
          // Enter or Space
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            open(e);
          }
        };

        opener.addEventListener("click", onClick);
        opener.addEventListener("keydown", onKeyDown);

        this._listeners.push(["click", opener, onClick]);
        this._listeners.push(["keydown", opener, onKeyDown]);
      });
    }

    _wireTouchMenus() {
      const isTouch =
        "ontouchstart" in window || (window.DocumentTouch && document instanceof DocumentTouch);

      if (!isTouch) return;

      const closeTouchSubmenus = (focusedElm, focusedParent = null) => {
        document.querySelectorAll(".style--classic li.focus").forEach((elm) => {
          if (!(elm === focusedElm || elm === focusedParent)) elm.classList.remove("focus");
        });
      };

      let babyMenuTouch = false;

      // baby menu triggers (classic)
      document
        .querySelectorAll(".style--classic .has-babymenu > a, .style--classic .has-babymenu > button")
        .forEach((trigger) => {
          const li = trigger.parentElement;
          const onClick = (e) => {
            // only intercept when it actually opens a submenu
            if (!li) return;
            e.preventDefault();
            e.stopPropagation();
            li.classList.toggle("focus");
            babyMenuTouch = true;
            closeTouchSubmenus(li, li.closest(".has-submenu"));
          };
          trigger.addEventListener("click", onClick);
          this._listeners.push(["click", trigger, onClick]);
        });

      // top-level submenu triggers (classic)
      document
        .querySelectorAll(".style--classic .has-submenu > a, .style--classic .has-submenu > button")
        .forEach((trigger) => {
          const li = trigger.parentElement;
          if (!li || li.classList.contains("mega-link")) return;

          const onClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!babyMenuTouch) {
              li.classList.toggle("focus");
              closeTouchSubmenus(li);
            }
            babyMenuTouch = false;
          };
          trigger.addEventListener("click", onClick);
          this._listeners.push(["click", trigger, onClick]);
        });
    }

    _wireKeyboardMenus() {
      // helpers
      const setExpanded = (trigger, expanded) => {
        if (!trigger) return;
        trigger.setAttribute("aria-expanded", expanded ? "true" : "false");
      };

      const openCloseOnEnter = (e, li, trigger) => {
        if (e.keyCode !== window.KEYCODES?.RETURN) return;
        // For links that navigate, only prevent default if it's meant to open submenu
        if (!trigger.classList.contains("no-focus-link")) e.preventDefault();

        const isOpen = li.classList.contains("focus");
        if (!isOpen) {
          li.classList.add("focus");
          setExpanded(trigger, true);
        } else {
          li.classList.remove("focus");
          setExpanded(trigger, false);
        }
      };

      // Top-level submenu triggers (classic)
      document
        .querySelectorAll(".site-nav.style--classic .has-submenu > a, .site-nav.style--classic .has-submenu > button")
        .forEach((trigger) => {
          const li = trigger.parentNode;

          const onKeyDown = (e) => openCloseOnEnter(e, li, trigger);
          trigger.addEventListener("keydown", onKeyDown);
          this._listeners.push(["keydown", trigger, onKeyDown]);

          // Close when tabbing out of submenu
          const lastLink =
            li.querySelector(".submenu-holder > li:last-child a, .submenu-holder > li:last-child button") ||
            li.querySelector(".submenu-holder a:last-child, .submenu-holder button:last-child");

          if (lastLink) {
            const onFocusOut = () => {
              if (li.classList.contains("focus")) {
                li.classList.remove("focus");
                setExpanded(trigger, false);
              }
            };
            lastLink.addEventListener("focusout", onFocusOut);
            this._listeners.push(["focusout", lastLink, onFocusOut]);
          }
        });

      // Baby menus (non-mega-link)
      document
        .querySelectorAll(".site-nav.style--classic .has-babymenu:not(.mega-link) > a, .site-nav.style--classic .has-babymenu:not(.mega-link) > button")
        .forEach((trigger) => {
          const li = trigger.parentNode;

          const onKeyDown = (e) => openCloseOnEnter(e, li, trigger);
          trigger.addEventListener("keydown", onKeyDown);
          this._listeners.push(["keydown", trigger, onKeyDown]);

          const lastBaby =
            li.querySelector(".babymenu li:last-child a, .babymenu li:last-child button") ||
            li.querySelector(".babymenu a:last-child, .babymenu button:last-child");

          if (lastBaby) {
            const onFocusOut = () => {
              const parentLi = li.parentNode; // original code used parentNode focus class
              if (parentLi?.classList.contains("focus")) {
                parentLi.classList.remove("focus");
                setExpanded(trigger, false);
              }
            };
            lastBaby.addEventListener("focusout", onFocusOut);
            this._listeners.push(["focusout", lastBaby, onFocusOut]);
          }
        });
    }

    _checkMenuWidth() {
      const header = document.querySelector(".site-header--menu-classic");
      if (!header) return;

      const classicMenu = header.querySelector(".area--menu-classic");
      if (!classicMenu) return;

      const logo = header.querySelector(".area--logo");
      const logoIsCentered = header.classList.contains("header__top--logo-center");
      let availWidthforClassicMenu = 0;

      const headerStyle = window.getComputedStyle(header);
      const headerPaddingRight = parseFloat(headerStyle.getPropertyValue("padding-right"));
      const headerPaddingLeft = parseFloat(headerStyle.getPropertyValue("padding-left"));
      const headerRowGap = 10;

      const headerWidth = header.offsetWidth - headerPaddingLeft - headerPaddingRight;
      const logoWidth = logo?.offsetWidth || 0;
      const classicMenuWidth = classicMenu.offsetWidth + headerRowGap;

      const headerItems = header.children;

      let totalWidthOfHeaderItems = 0;
      for (let i = 0; i < headerItems.length; i++) {
        if (headerItems[i].offsetWidth > 0) totalWidthOfHeaderItems += headerItems[i].offsetWidth + headerRowGap;
      }
      totalWidthOfHeaderItems -= headerRowGap;

      if (logoIsCentered) {
        availWidthforClassicMenu = headerWidth / 2 - logoWidth / 2 - headerRowGap;
      } else {
        const otherElementsWidth = totalWidthOfHeaderItems - logoWidth - classicMenuWidth;
        availWidthforClassicMenu = headerWidth - otherElementsWidth - logoWidth;
      }

      if (classicMenuWidth > availWidthforClassicMenu) {
        header.classList.remove("site-header--menu-classic");
        header.classList.add("site-header--menu-minimal");
        classicMenu.remove();
      }
    }

    _adjustNavLayout() {
      if (this.querySelector(".site-nav") && this.querySelector(".site-nav").classList.contains("style--classic")) {
        if (this.querySelector("nav > ul").scrollWidth > this.querySelector("nav").offsetWidth) {
          this.classList.add("site-header--swap-menu-style");
          document.body.classList.remove("header--has-two-rows");

          const header = document.getElementById("site-header");
          header?.querySelectorAll('[class*="two-rows"]').forEach((el) => {
            el.className = el.className.replace("two-rows", "one-row");
          });
        }
      }
    }
  }

  if (typeof customElements.get("main-header") === "undefined") {
    customElements.define("main-header", MainHeader);
  }
}

if (typeof SidebarDrawer !== "function") {
  class SidebarDrawer extends HTMLElement {
    constructor() {
      super();

      // default aria state
      if (!this.hasAttribute("aria-hidden")) this.setAttribute("aria-hidden", "true");

      const closeBtn = this.querySelector("[data-js-close]");
      if (closeBtn) {
        closeBtn.addEventListener("click", () => this.hide());
      }

      // One ESC listener per instance is OK (small), but respects current structure.
      document.addEventListener("keydown", (e) => {
        if (e.keyCode === window.KEYCODES?.ESC) {
          const openedSidebar = document.querySelector("sidebar-drawer.sidebar--opened");
          if (openedSidebar) openedSidebar.hide();
        }
      });
    }

    show() {
      this._opened = true;

      document.body.classList.add("sidebar-opened");
      if (this.classList.contains("sidebar--right")) document.body.classList.add("sidebar-opened--right");
      if (this.classList.contains("sidebar--left")) document.body.classList.add("sidebar-opened--left");

      if (this.id === "site-cart-sidebar" && document.getElementById("cart-recommendations-sidebar")) {
        setTimeout(() => {
          document.querySelector("#cart-recommendations-sidebar css-slider")?.resetSlider();
        }, 10);
      }

      this.style.display = "grid";
      this.setAttribute("aria-hidden", "false");

      setTimeout(() => {
        this.classList.add("sidebar--opened");
        if (window.inertElems) {
          window.inertElems.forEach((elm) => elm.setAttribute("inert", ""));
        }
      }, 15);
    }

    hide() {
      this._opened = false;
      this.classList.remove("sidebar--opened");

      document.body.classList.remove("sidebar-opened", "sidebar-opened--left", "sidebar-opened--right");

      if (window.inertElems) {
        window.inertElems.forEach((elm) => elm.removeAttribute("inert"));
      }

      this.setAttribute("aria-hidden", "true");

      // restore aria-expanded and focus to the opener if we have it
      const opener = this._lastOpener || document.querySelector(`[aria-controls="${this.id}"]`);
      opener?.setAttribute("aria-expanded", "false");
      setTimeout(() => opener?.focus?.(), 0);

      setTimeout(() => {
        this.style.display = "none";
      }, 501);
    }
  }

  if (typeof customElements.get("sidebar-drawer") === "undefined") {
    customElements.define("sidebar-drawer", SidebarDrawer);
  }
}

if (typeof MobileNavigation !== "function") {
  class MobileNavigation extends HTMLElement {
    constructor() {
      super();

      this._openedFirstSubmenu = false;
      this._openedSecondSubmenu = false;

      // Support either <a> or <button> as triggers
      this.querySelectorAll(".has-submenu > a, .has-submenu > button").forEach((trigger) => {
        const onActivate = (e) => {
          // Only prevent if it's meant to open submenu
          e.preventDefault();

          if (!this._openedFirstSubmenu) {
            this._openedFirstSubmenu = true;
            this.classList.add("opened-first-submenu");
            this.closest("sidebar-drawer")?.scrollTo({ top: 0 });
          }

          trigger.closest("li")?.classList.add("opened");
          this._resizeContainer();
        };

        trigger.addEventListener("click", onActivate);

        trigger.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onActivate(e);
          }
        });
      });

      this.querySelectorAll(".has-babymenu > a, .has-babymenu > button").forEach((trigger) => {
        const onActivate = (e) => {
          e.preventDefault();

          if (!this._openedSecondSubmenu) {
            this._openedSecondSubmenu = true;
            this.classList.add("opened-second-submenu");
            this.closest("sidebar-drawer")?.scrollTo({ top: 0 });
          }

          trigger.closest("li")?.classList.add("opened");
          this._resizeContainer();
        };

        trigger.addEventListener("click", onActivate);

        trigger.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onActivate(e);
          }
        });
      });

      // Back buttons (your Liquid uses <button>)
      this.querySelectorAll(".submenu-back button, .submenu-back a").forEach((backEl) => {
        const onBack = (e) => {
          e.preventDefault();

          if (this._openedSecondSubmenu) {
            this._openedSecondSubmenu = false;
            this.classList.remove("opened-second-submenu");
            this._resizeContainer();
          } else if (this._openedFirstSubmenu) {
            this._openedFirstSubmenu = false;
            this.classList.remove("opened-first-submenu");
            this._resizeContainer(true);
          }

          this.closest("sidebar-drawer")?.scrollTo({ top: 0 });

          setTimeout(() => {
            this.querySelector("li.opened")?.classList.remove("opened");
          }, 301);
        };

        backEl.addEventListener("click", onBack);
        backEl.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onBack(e);
          }
        });
      });
    }

    _resizeContainer(main = false) {
      if (main) {
        this.style.height = "auto";
        return;
      }

      if (this._openedSecondSubmenu) {
        const bm = this.querySelector(".has-babymenu.opened .babymenu");
        if (bm) this.style.height = `${bm.scrollHeight}px`;
      } else if (this._openedFirstSubmenu) {
        const sm = this.querySelector(".has-submenu.opened .submenu");
        if (sm) this.style.height = `${sm.scrollHeight}px`;
      }
    }
  }

  if (typeof customElements.get("mobile-navigation") === "undefined") {
    customElements.define("mobile-navigation", MobileNavigation);
  }
}
